import classNames from 'classnames';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { Heading, List } from 'yoso';
import { Badge } from '~/components/Badge/Badge';
import { useChallengeEventHistories } from '~/components/Challenge/Event/ChallengeEvent.hooks';
import { BaseError } from '~/components/Error/BaseError';
import { Celect, CelectProps } from '~/components/Form/Celect';
import { T } from '~/components/I18n';
import styles from '../ChallengeEventCelect.module.scss';

export type ChallengeParticipatedEventCelectProps = Pick<
  CelectProps,
  'value' | 'className' | 'onChange' | 'placement'
>;

export const ChallengeParticipatedEventCelect = ({
  className,
  value,
  onChange,
  placement,
}: ChallengeParticipatedEventCelectProps) => {
  const { data, hasNextPage, fetchNextPage, isFetched } = useChallengeEventHistories();

  const nextInView = useInView({
    onChange: (inView) => inView && fetchNextPage(),
  });

  const items = useMemo(
    () => data?.pages.flatMap(({ histories }) => histories) || [],
    [data?.pages]
  );

  if (items.length === 0 && isFetched) {
    throw new BaseError('EMPTY');
  }

  return (
    <Celect.Root
      className={classNames(styles.root, className)}
      value={value}
      onChange={onChange}
      placement={placement}>
      <Celect.Button
        variant="text"
        className={styles.button}
        data-event="click"
        data-event-target="selectround_in_challengehistory">
        <T name="citizen_challenge_round" options={[value]} />
      </Celect.Button>
      <Celect.Modal className={styles.modal}>
        <Heading level={5} className={styles.heading}>
          <T name="citizen_challenge_history_round_title" />
        </Heading>
        <List className={styles.list}>
          {items.map(({ week, startAt, endAt, weekStatus }) => (
            <li key={week}>
              <Celect.Option value={week.toString()} className={styles.option}>
                <Heading level={6} className={styles.week}>
                  <T name="citizen_challenge_round" options={[week]} />{' '}
                  {weekStatus === 'IN_PROGRESS' && (
                    <Badge color="red" variant="pill" className={styles.badge}>
                      <T name="citizen_status_label_ongoing" />
                    </Badge>
                  )}
                </Heading>
                <p className={styles.description}>
                  {format(startAt, 'PP')} ~ {format(endAt, 'PP')}
                </p>
              </Celect.Option>
            </li>
          ))}
          {hasNextPage && (
            <li>
              <div ref={nextInView.ref} className={styles.placeholder}>
                More
              </div>
            </li>
          )}
        </List>
      </Celect.Modal>
    </Celect.Root>
  );
};
